import { Component, Input, OnChanges, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import * as FileSaver from "file-saver";
import {
  AuthenticationService,
  GMUser,
} from "../authentication/authentication/authentication.service";
import { ErrorHandlerDialogComponent } from "../error-handler-dialog/error-handler-dialog.component";
import { ReaderService } from "../../services/reader.service";
import { WriterService } from "../../services/writer.service";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { DynamicHomepageService } from "../../services/dynamic-homepage.service";

@Component({
    selector: "app-toolbar",
    templateUrl: "./toolbar.component.html",
    styleUrls: ["./toolbar.component.scss"],
    standalone: false
})
export class ToolbarComponent implements OnChanges, OnInit, OnDestroy {
  title = "Competitor Product Database";
  subscription: string;
  subscribe = "Subscribe to Segment Changes";
  unsubscribe = "Unsubscribe from Segment Changes";
  user: GMUser;
  record;
  currentUrl = "";
  @Input() ready = false;
  editingHomepage = false;
  allowHomepageEdit = false;
  private editingHomepageSub: Subscription;

  constructor(
    private authService: AuthenticationService,
    private readerService: ReaderService,
    private writerService: WriterService,
    private homepageService: DynamicHomepageService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.user = authService.userInfo;
  }

  navItems = [
    {
      label: "Views",
      children: [
        { label: "Vehicle Search", route: "view/vehicles" },
        { label: "Body Style Life Cycles", route: "view/bodystyles" },
        { label: "Propulsion", route: "view/propulsions" },
      ],
    },
    {
      label: "Reports",
      children: [
        { label: "Vehicle Rollout", route: "reports/vehicleRollout" },
        { label: "Competitor Vehicles", route: "reports/competitorVehicles" },
        {
          label: "Propulsion Rollout",
          route: "reports/propulsionRollout",
        },
        {
          label: "Competitive Activity Landscape",
          route: "reports/competitiveActivityLandscape",
        },
      ],
    },
    {
      label: "Help",
      children: [
        {
          label: "User Guide",
          helpDoc: "user-guide",
          fileName: "CPD Reference Guide - USER.pptx",
        },
        {
          label: "Editor Guide",
          helpDoc: "editor-guide",
          fileName: "CPD Reference Guide - EDITOR.pptx",
        },
        {
          label: "CPD POWER BI DASHBOARD",
          href:
            "https://app.powerbi.com/groups/8bf0c03e-a842-41d8-b189-d4c8e6fb5e5c/reports/2173ff83-3ae4-4124-9573-a3dbcf6b4f14/ReportSection01bb646c78c3b8301db0?ctid=5de110f8-2e0f-4d45-891d-bcf2218e253d&experience=power-bi",
        },
      ],
    },
    { label: "Glossary of CPD Terms", route: "editabledocs/cpdglossary" },
  ];

  ngOnInit() {
    this.editingHomepageSub = this.homepageService.editingHomepage.subscribe(
      (res) => {
        this.editingHomepage = res;
      }
    );
    this.router.events.subscribe((val) => {
      this.allowHomepageEdit =
        this.authService.gmUser.admin && this.router.url === "/";
      this.currentUrl = this.router.url;
    });
  }

  ngOnDestroy() {
    this.editingHomepageSub?.unsubscribe();
  }

  ngOnChanges() {
    if (this.authService.userInfo) {
      this.user = this.authService.userInfo;
      this.record = {
        createdby: this.user.email,
        viewtype: "vehicle",
        field: "globalsegment",
      };
      this.readerService
        .getAllFromTable(
          "user_subscription?id=" +
            this.user.email +
            "&id=vehicle&id=globalsegment"
        )
        .subscribe((response) => {
          this.subscription =
            response == null ? this.subscribe : this.unsubscribe;
        });
      if (this.user.admin) {
        this.navItems[1].children[this.navItems[1].children.length] = {
          label: "Vehicle Version",
          route: "reports/vehicleVersions",
        };
        this.navItems.push({
          label: "References",
          children: [
            { label: "APL Status", route: "/reference/status" },
            {
              label: "Architecture",
              route: "/reference/architecture",
            },
            {
              label: "Architecture Type",
              route: "/reference/architecturetype",
            },
            { label: "Bodystyle", route: "/reference/bodystylevalue" },
            { label: "Brand", route: "/reference/brand" },
            { label: "Drive Configuration", route: "/reference/driveconfig" },
            {
              label: "Engineering Class",
              route: "/reference/engineeringclass",
            },
            { label: "Global Segment", route: "/reference/globalsegment" },
            { label: "OEM", route: "/reference/oem" },
            { label: "Propulsion Type", route: "/reference/propulsiontype" },
            { label: "Region", route: "/reference/region" },
            // { label: 'Vehicle Type'                , route: '/reference/vehicletype'        },
            {
              label: "Vehicle Launch Probability",
              route: "/reference/launchprobability",
            },
          ],
        });
      }
      this.allowHomepageEdit =
        this.authService.gmUser.admin && this.currentUrl === "/";
    }
  }

  updateSubscription() {
    if (this.subscription === this.subscribe) {
      this.writerService
        .updateSegmentChangeSubscription(this.record, "subscribe")
        .subscribe(() => {
          this.subscription = this.unsubscribe;
          this.dialog.open(ErrorHandlerDialogComponent, {
            data: {
              message: "Subscribed to Segment changes",
              status: "Success",
            },
          });
        });
    } else {
      this.writerService
        .updateSegmentChangeSubscription(this.record, "unsubscribe")
        .subscribe(() => {
          this.subscription = this.subscribe;
          this.dialog.open(ErrorHandlerDialogComponent, {
            data: {
              message: "Unsubscribed from Segment changes",
              status: "Success",
            },
          });
        });
    }
  }

  toggleHomepageEditor() {
    this.homepageService.enableHomepageEditor();
  }

  openExternalLink(url: string): void {
    if (!url) {
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  openHelpDocument(doc: string, fileName?: string): void {
    if (!doc) {
      return;
    }
    this.readerService.getHelpDocument(doc).subscribe({
      next: (blob) => {
        FileSaver.saveAs(blob, fileName || doc);
      },
      error: (error) => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message: "Unable to download help document. Please try again.",
            status: error.error == null ? error.status : error.error.status,
          },
        });
      },
    });
  }
}
